import { type Interpolator, toCircle } from 'flubber'
import { useEffect, useRef } from 'react'

import { Container } from '@/components/ui/container'
import { cn } from '@/lib/utils'

/** Original logo paths, in viewBox "0 0 99 56" space. */
const LOGO_PATHS = [
  'M20.2998 0.800002C20.2998 0.800002 38.4997 0.800002 38.5 16.8996C38.5 32.9996 24.5 32.5529 24.5 32.5529L40 48.5002L33 55.5002L10.4004 32.7004V54.5998H0V0.800002H20.2998ZM19.7998 10.0002H10.4004V23.5002H19.7998C19.7998 23.5002 28.1996 24.1 28.0996 16.8C27.9996 9.50001 19.7998 10.0002 19.7998 10.0002Z',
  'M69.8 0C54.5 0 42.1 12.4 42.1 27.7C42.1 43 54.5 55.4 69.8 55.4C85.1 55.4 97.5 43 97.5 27.7C97.5 12.4 85.1 0 69.8 0ZM69.8 45.2C60.1 45.2 52.3 37.4 52.3 27.7C52.3 18 60.1 10.2 69.8 10.2C79.5 10.2 87.3 18 87.3 27.7C87.3 37.4 79.5 45.2 69.8 45.2Z',
  'M75.4031 25.4996L68.3311 32.5706L91.1704 55.4132L98.2424 48.3422L75.4031 25.4996Z',
]

/** fill-rule per path so the resting logo shows its counters. */
const FILL_RULES = ['evenodd', 'evenodd', 'nonzero'] as const

interface Center {
  readonly cx: number
  readonly cy: number
}

/** Target circle centers, evenly spaced and vertically centered. */
const CENTERS: Center[] = [
  { cx: 21.25, cy: 28 },
  { cx: 49.5, cy: 28 },
  { cx: 77.75, cy: 28 },
]

const RADIUS = 7

const INTRO_S = 0.5 // logo hold before the morph begins
const MORPH_S = 0.275 // base morph duration
const LAG_S = 0.14 // per-path stagger
const PERIOD_S = 0.6 // bounce cycle length
const AMPLITUDE = 9 // bounce height, viewBox units

/**
 * Cubic ease-in-out: remaps a linear 0..1 `progress` to an eased 0..1 curve
 * that starts slow, speeds up through the middle, and slows again at the end —
 * so the morph accelerates away from the logo and settles gently into the
 * circle rather than moving at a constant rate.
 *
 * The two halves are mirror images that meet at (0.5, 0.5):
 *  - First half  (progress < 0.5): `4 * progress³` accelerates up from 0.
 *  - Second half (progress ≥ 0.5): `1 - (-2·progress + 2)³ / 2` decelerates into 1.
 */
const easeInOutCubic = (progress: number): number =>
  progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2

/**
 * Vertical bounce offset for a given time (seconds) and phase shift.
 * Uses |cos| so the cycle starts at the top of the arc and falls from there.
 * Returns a value in [-AMPLITUDE, 0].
 */
const bounceDy = (timeSeconds: number, phase: number): number => {
  const cyclePosition = (((timeSeconds / PERIOD_S + phase) % 1) + 1) % 1
  return -AMPLITUDE * Math.abs(Math.cos(cyclePosition * Math.PI))
}

/** Per-path morph schedule: all paths start together, end staggered. */
const morphSchedule = (
  index: number,
): { begin: number; end: number; duration: number } => {
  const begin = INTRO_S
  const end = INTRO_S + index * LAG_S + MORPH_S
  return { begin, end, duration: end - begin }
}

export interface Props extends React.HTMLAttributes<HTMLElement> {
  /**
   * When `true`, the logo holds briefly, morphs into the three circles, then
   * bounces. When `false` (default), the bouncing circles render directly with
   * no morph step.
   */
  animateFromLogo?: boolean
  /**
   * Fill color of the first two shapes. The third is always the blue accent.
   * @default "black"
   */
  variant?: 'black' | 'white'
}

const ACCENT = 'url(#blueSlashGradient)'
type ShapeEl = SVGPathElement | SVGCircleElement

const LoadingWidget: React.FC<Props> = ({
  animateFromLogo = false,
  variant = 'black',
  className,
  ...props
}) => {
  const baseFill = variant === 'white' ? '#ffffff' : '#2b3344'
  const fills: readonly string[] = [baseFill, baseFill, ACCENT]

  // One ref per shape element (path when morphing, circle otherwise).
  const shapeRefs = useRef<Array<ShapeEl | null>>([null, null, null])

  useEffect(() => {
    const elements = shapeRefs.current
    if (elements.some((element) => element === null)) return

    let animationFrameId = 0
    let startTime = 0

    // Build morph interpolator only when needed.
    const morphInterpolators: Array<Interpolator | null> | null =
      animateFromLogo
        ? CENTERS.map((center, index) => {
            const fromPath = LOGO_PATHS[index]
            return fromPath
              ? toCircle(fromPath, center.cx, center.cy, RADIUS, {
                  maxSegmentLength: 2,
                })
              : null
          })
        : null

    function applyTransform(element: ShapeEl, center: Center, offsetY: number) {
      // bounce + a subtle squash at the bottom of the arc
      const lift = -offsetY / AMPLITUDE // 0..1
      const squash = 1 - 0.12 * (1 - lift)
      element.setAttribute(
        'transform',
        `translate(0 ${offsetY}) translate(${center.cx} ${center.cy}) scale(1 ${squash}) translate(${-center.cx} ${-center.cy})`,
      )
    }

    function renderFrame(now: number) {
      if (startTime === 0) startTime = now
      const elapsedSeconds = (now - startTime) / 1000

      for (let index = 0; index < CENTERS.length; index++) {
        const element = elements[index]
        const center = CENTERS[index]
        if (!element || !center) continue

        if (morphInterpolators) {
          // ---- morph branch ----
          const interpolate = morphInterpolators[index]
          if (!interpolate) continue
          const { begin, duration, end } = morphSchedule(index)
          const path = element as SVGPathElement

          if (elapsedSeconds < begin) {
            // intro hold — keep the crisp original logo. (Calling interpolate(0)
            // here would replace it with flubber's single-ring polygon, which
            // drops the R/Q counters and looks janky.)
            applyTransform(path, center, -AMPLITUDE)
          } else if (elapsedSeconds < end) {
            // morphing — held at the bounce peak
            const progress = Math.min(1, (elapsedSeconds - begin) / duration)
            path.setAttribute('d', interpolate(easeInOutCubic(progress)))
            applyTransform(path, center, -AMPLITUDE)
          } else {
            // morph finished — lock the circle and bounce from the peak
            path.setAttribute('d', interpolate(1))
            applyTransform(path, center, bounceDy(elapsedSeconds - end, 0))
          }
        } else {
          // ---- circles-only branch: bounce immediately, phase-staggered ----
          const phase = (index * LAG_S) / PERIOD_S
          applyTransform(element, center, bounceDy(elapsedSeconds, phase))
        }
      }

      animationFrameId = requestAnimationFrame(renderFrame)
    }

    animationFrameId = requestAnimationFrame(renderFrame)
    return () => cancelAnimationFrame(animationFrameId)
  }, [animateFromLogo])

  const peakTransform = `translate(0 ${-AMPLITUDE})`

  return (
    <Container
      className={cn(className, 'flex h-full justify-center p-12')}
      {...props}
    >
      <div className="my-auto">
        <div className="overflow-visible">
          <svg
            className="w-60"
            width="99"
            height="56"
            viewBox="0 0 99 56"
            fill="none"
            overflow="visible"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Loading"
          >
            <defs xmlns="http://www.w3.org/2000/svg">
              <linearGradient
                id="blueSlashGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#1EA7FF" />
                <stop offset="45%" stopColor="#39BDF2" />
                <stop offset="100%" stopColor="#9BE6D6" />
              </linearGradient>
            </defs>

            {CENTERS.map((center, index) =>
              animateFromLogo ? (
                <path
                  key={index}
                  ref={(element) => {
                    shapeRefs.current[index] = element
                  }}
                  d={LOGO_PATHS[index]}
                  fill={fills[index]}
                  fillRule={FILL_RULES[index]}
                  transform={peakTransform}
                />
              ) : (
                <circle
                  key={index}
                  ref={(element) => {
                    shapeRefs.current[index] = element
                  }}
                  cx={center.cx}
                  cy={center.cy}
                  r={RADIUS}
                  fill={fills[index]}
                  transform={peakTransform}
                />
              ),
            )}
          </svg>
        </div>
      </div>
    </Container>
  )
}

export default LoadingWidget
